<template>
  <div class="contact-list">
    <el-card>
      <div slot="header" class="card-header">
        <span class="header-title">联系人列表</span>
        <el-button type="primary" size="small" class="add-btn" @click="showAddDialog">添加联系人</el-button>
      </div>

      <!-- 搜索框 -->
      <div class="search-box">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索姓名或电话号码"
          clearable
          @input="handleSearch"
          class="search-input"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>
      </div>

      <!-- 桌面端表格视图 -->
      <el-table
        v-if="!isMobile"
        :data="contacts"
        stripe
        class="contact-table"
        v-loading="loading"
      >
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="姓名" width="150"></el-table-column>
        <el-table-column prop="phone" label="电话号码" width="180"></el-table-column>
        <el-table-column prop="email" label="邮箱" width="200"></el-table-column>
        <el-table-column prop="address" label="地址"></el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)">修改</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 移动端卡片列表视图 -->
      <div v-if="isMobile" class="mobile-contact-list" v-loading="loading">
        <div v-if="contacts.length === 0" class="empty-state">
          <p>暂无联系人</p>
        </div>
        <el-card
          v-for="contact in contacts"
          :key="contact.id"
          class="mobile-contact-card"
          shadow="hover"
        >
          <div class="mobile-card-content">
            <div class="mobile-card-header">
              <div class="contact-info">
                <div class="contact-name">{{ contact.name }}</div>
                <div class="contact-phone">{{ contact.phone }}</div>
              </div>
            </div>
            <div class="mobile-card-body">
              <div v-if="contact.email" class="contact-detail">
                <i class="el-icon-message"></i>
                <span>{{ contact.email }}</span>
              </div>
              <div v-if="contact.address" class="contact-detail">
                <i class="el-icon-location-outline"></i>
                <span>{{ contact.address }}</span>
              </div>
            </div>
            <div class="mobile-card-actions">
              <el-button size="mini" type="primary" @click="handleEdit(contact)">修改</el-button>
              <el-button size="mini" type="danger" @click="handleDelete(contact)">删除</el-button>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 添加/修改对话框 -->
      <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        :width="dialogWidth"
        :fullscreen="isMobile"
        @close="resetForm"
        class="contact-dialog"
      >
        <el-form
          :model="contactForm"
          :rules="rules"
          ref="contactForm"
          :label-width="isMobile ? '80px' : '100px'"
        >
          <el-form-item label="姓名" prop="name">
            <el-input v-model="contactForm.name" placeholder="请输入姓名"></el-input>
          </el-form-item>
          <el-form-item label="电话号码" prop="phone">
            <el-input v-model="contactForm.phone" placeholder="请输入电话号码"></el-input>
          </el-form-item>
          <el-form-item label="邮箱" prop="email">
            <el-input v-model="contactForm.email" placeholder="请输入邮箱（可选）"></el-input>
          </el-form-item>
          <el-form-item label="地址" prop="address">
            <el-input
              v-model="contactForm.address"
              type="textarea"
              :rows="3"
              placeholder="请输入地址（可选）"
            ></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </div>
      </el-dialog>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'ContactList',
  data() {
    return {
      contacts: [],
      loading: false,
      searchKeyword: '',
      dialogVisible: false,
      dialogTitle: '添加联系人',
      isEdit: false,
      currentId: null,
      contactForm: {
        name: '',
        phone: '',
        email: '',
        address: ''
      },
      rules: {
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入电话号码', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$/, message: '电话号码格式不正确', trigger: 'blur' }
        ]
      },
      windowWidth: window.innerWidth
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768
    },
    dialogWidth() {
      return this.isMobile ? '95%' : '500px'
    }
  },
  mounted() {
    this.loadContacts()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 处理窗口大小变化
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    // 加载联系人列表
    async loadContacts() {
      this.loading = true
      try {
        const response = await this.$http.get('/contacts')
        if (response.data.success) {
          this.contacts = response.data.data
        } else {
          this.$message.error(response.data.message || '加载失败')
        }
      } catch (error) {
        this.$message.error('加载联系人失败: ' + (error.message || '网络错误'))
      } finally {
        this.loading = false
      }
    },
    // 搜索联系人
    async handleSearch() {
      if (!this.searchKeyword || this.searchKeyword.trim() === '') {
        this.loadContacts()
        return
      }
      this.loading = true
      try {
        const response = await this.$http.get('/contacts/search', {
          params: { keyword: this.searchKeyword }
        })
        if (response.data.success) {
          this.contacts = response.data.data
        } else {
          this.$message.error(response.data.message || '搜索失败')
        }
      } catch (error) {
        this.$message.error('搜索失败: ' + (error.message || '网络错误'))
      } finally {
        this.loading = false
      }
    },
    // 显示添加对话框
    showAddDialog() {
      this.dialogTitle = '添加联系人'
      this.isEdit = false
      this.currentId = null
      this.dialogVisible = true
    },
    // 修改联系人
    async handleEdit(row) {
      this.dialogTitle = '修改联系人'
      this.isEdit = true
      this.currentId = row.id
      this.loading = true
      try {
        // 从后端数据库读取最新数据
        const response = await this.$http.get(`/contacts/${row.id}`)
        if (response.data.success) {
          const contact = response.data.data
          this.contactForm = {
            name: contact.name,
            phone: contact.phone,
            email: contact.email || '',
            address: contact.address || ''
          }
          this.dialogVisible = true
        } else {
          this.$message.error(response.data.message || '加载联系人信息失败')
        }
      } catch (error) {
        this.$message.error('加载联系人信息失败: ' + (error.message || '网络错误'))
      } finally {
        this.loading = false
      }
    },
    // 删除联系人
    handleDelete(row) {
      this.$confirm(`确定要删除联系人 "${row.name}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$http.delete(`/contacts/${row.id}`)
          if (response.data.success) {
            this.$message.success('删除成功')
            this.loadContacts()
          } else {
            this.$message.error(response.data.message || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败: ' + (error.message || '网络错误'))
        }
      }).catch(() => {
        // 用户取消删除
      })
    },
    // 提交表单
    handleSubmit() {
      this.$refs.contactForm.validate(async (valid) => {
        if (valid) {
          try {
            let response
            if (this.isEdit) {
              // 修改联系人
              response = await this.$http.put(`/contacts/${this.currentId}`, this.contactForm)
            } else {
              // 添加联系人
              response = await this.$http.post('/contacts', this.contactForm)
            }
            if (response.data.success) {
              this.$message.success(this.isEdit ? '修改成功' : '添加成功')
              this.dialogVisible = false
              this.loadContacts()
            } else {
              this.$message.error(response.data.message || '操作失败')
            }
          } catch (error) {
            this.$message.error('操作失败: ' + (error.response?.data?.message || error.message || '网络错误'))
          }
        } else {
          return false
        }
      })
    },
    // 重置表单
    resetForm() {
      this.$refs.contactForm && this.$refs.contactForm.resetFields()
      this.contactForm = {
        name: '',
        phone: '',
        email: '',
        address: ''
      }
      this.isEdit = false
      this.currentId = null
    }
  }
}
</script>

<style scoped>
.contact-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 10px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 500;
}

.add-btn {
  white-space: nowrap;
}

.search-box {
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
  max-width: 300px;
}

.contact-table {
  width: 100%;
  margin-top: 20px;
}

/* 移动端样式 */
.mobile-contact-list {
  margin-top: 20px;
}

.mobile-contact-card {
  margin-bottom: 12px;
  border-radius: 8px;
}

.mobile-contact-card:last-child {
  margin-bottom: 0;
}

.mobile-card-content {
  padding: 0;
}

.mobile-card-header {
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 12px;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.contact-phone {
  font-size: 14px;
  color: #606266;
}

.mobile-card-body {
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.contact-detail {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
}

.contact-detail:last-child {
  margin-bottom: 0;
}

.contact-detail i {
  margin-top: 2px;
  color: #909399;
}

.contact-detail span {
  flex: 1;
  word-break: break-all;
}

.mobile-card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

/* 响应式样式 */
@media screen and (max-width: 767px) {
  .contact-list {
    padding: 5px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-title {
    font-size: 16px;
  }

  .add-btn {
    width: 100%;
  }

  .search-input {
    max-width: 100%;
  }

  .contact-dialog {
    margin: 0;
  }

  .contact-dialog >>> .el-dialog {
    margin: 0;
    width: 100% !important;
    height: 100%;
    border-radius: 0;
  }

  .contact-dialog >>> .el-dialog__body {
    padding: 15px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
}

@media screen and (min-width: 768px) and (max-width: 1024px) {
  .contact-list {
    padding: 15px;
  }

  .search-input {
    max-width: 100%;
  }
}
</style>

